import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, useTenant } from '../../lib/api';
import { useRole } from '../../lib/role';

type SwapOut = { zone_id: string; nameservers: string[]; ns_status: string; job_id: string | null };

export default function DomainSwap() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { data: t } = useTenant(id);
  const { isAdmin } = useRole();
  const [to, setTo] = useState('');
  const [renamePersonas, setRenamePersonas] = useState(false);
  const [nameSeed, setNameSeed] = useState(t?.name_seed || '');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SwapOut | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (!t) return null;
  const from = t.target_domain;
  const pending = t.pending_swap;

  async function go() {
    if (confirm !== to) { setErr('type the new domain to confirm'); return; }
    setBusy(true); setErr(null); setResult(null);
    try {
      const seed = renamePersonas ? (nameSeed.trim() || null) : undefined;
      const out = await api.domainSwap(id, {
        from_domain: from,
        to_domain: to,
        ...(seed !== undefined ? { name_seed: seed } : {}),
      });
      setResult(out);
      qc.invalidateQueries({ queryKey: ['tenant', id] });
    } catch (e: any) {
      setErr(e?.message || 'failed to start swap');
    } finally {
      setBusy(false);
    }
  }

  if (pending) {
    return (
      <div className="p-6 max-w-xl space-y-3">
        <div className="card p-5">
          <div className="text-xs uppercase tracking-wider text-dim mb-1">Swap already in flight</div>
          <div className="text-sm text-ink mb-2">
            A swap from <span className="font-mono">{pending.from_domain}</span> to{' '}
            <span className="font-mono">{pending.to_domain}</span> is{' '}
            {pending.ns_status === 'active'
              ? 'running now (Cloudflare delegation confirmed).'
              : pending.ns_status === 'timeout' || pending.ns_status === 'failed'
                ? `${pending.ns_status === 'timeout' ? 'timed out' : 'failed'} — see the live card on the Overview tab to re-check or restart.`
                : 'awaiting nameserver delegation.'}
          </div>
          <div className="text-xs text-dim">
            Live status, the nameserver pair, and the Check-now action are on the{' '}
            <strong>Overview</strong> tab. Starting a second swap before this one finishes
            isn't allowed.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-xl">
      <div className="card p-5 space-y-3">
        <div className="text-xs text-warn">
          {isAdmin
            ? "Destructive. Rewrites every mailbox's primary SMTP + tenant sign-in. Old addresses disappear."
            : 'Destructive. Old addresses disappear.'}
        </div>
        <div className="block">
          <div className="text-xs text-dim mb-1">From domain</div>
          <div className="input bg-bg text-dim cursor-not-allowed">{from}</div>
        </div>
        <label className="block">
          <div className="text-xs text-dim mb-1">To domain</div>
          <input
            className="input" value={to}
            onChange={e => setTo(e.target.value.trim().toLowerCase())}
            placeholder="newdomain.com"
            autoComplete="off"
          />
        </label>
        <div className="text-[12px] text-dim italic">
          The old domain will be detached from the tenant after the swap. Old addresses disappear,
          and every mailbox re-authenticates to your sequencer automatically under its new address.
        </div>
        <div className="border-t border-line pt-3 mt-1 space-y-2">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={renamePersonas}
              onChange={e => setRenamePersonas(e.target.checked)}
            />
            Also change the mailbox personas
          </label>
          {renamePersonas && (
            <>
              <input
                className="input"
                value={nameSeed}
                onChange={e => setNameSeed(e.target.value)}
                placeholder="John Doe (or leave blank to randomize)"
                autoComplete="off"
              />
              <div className="text-[11px] text-dim">
                One persona = all mailboxes become iterations of that name
                (alex@, agalloway@, a.galloway@, …). Comma-separated for a
                fair split. Blank = fully random names.
              </div>
            </>
          )}
        </div>
        <label className="block">
          <div className="text-xs text-dim mb-1">
            Type <code className="text-ink">{to || 'the new domain'}</code> to confirm
          </div>
          <input className="input" value={confirm} onChange={e => setConfirm(e.target.value)} />
        </label>
        {err && <div className="text-err text-xs">{err}</div>}
        <button
          onClick={go}
          disabled={busy || !to || confirm !== to || to === from}
          className="btn-danger"
        >
          {busy ? 'Starting…' : 'Run swap'}
        </button>
      </div>

      {result && (
        <div className="card p-5 mt-4 space-y-3 border border-accent/30">
          <div className="text-xs uppercase tracking-wider text-dim">
            {result.ns_status === 'active' ? 'Swap queued' : 'Awaiting nameserver delegation'}
          </div>
          {result.ns_status === 'active' ? (
            <div className="text-sm text-ink">
              Cloudflare already shows <span className="font-mono">{to}</span> as active.
              The swap job is enqueued and will run in the background — watch the tenant
              status on the Overview tab.
            </div>
          ) : (
            <>
              <div className="text-sm text-ink">
                At your domain registrar (GoDaddy, Namecheap, etc.), replace the existing
                nameservers for <span className="font-mono">{to}</span> with the two
                below. The swap runs automatically once delegation is confirmed.
              </div>
              <div className="space-y-2">
                {result.nameservers.map(ns => (
                  <div key={ns} className="flex items-center justify-between gap-3 rounded-md bg-white/[0.03] px-3 py-2 font-mono text-sm">
                    <span className="truncate">{ns}</span>
                    <button
                      onClick={() => navigator.clipboard?.writeText(ns)}
                      className="text-xs text-dim hover:text-ink"
                      title="Copy"
                    >
                      Copy
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
